import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  Layer,
  SelectionArea,
  PreviewDimensions,
  PatternElement,
} from '../types';
import { generatePatternElements } from '../utils/patternGenerator';
import { SelectionOverlay } from './SelectionOverlay';

interface PatternPreviewProps {
  layers: Layer[];
  width: number;
  height: number;
  backgroundImage: string | null;
  isSelectionMode: boolean;
  onSelectionComplete: (area: SelectionArea, layerId: string) => void;
  selectedLayerId?: string;
  onMaskMove?: (layerId: string, x: number, y: number) => void;
  onMaskResize?: (layerId: string, newArea: SelectionArea) => void;
  onDimensionsChange?: (dimensions: PreviewDimensions) => void;
  svgContent?: string; // 追加：SVG内容（隠蔽率計算用）
}

const PatternPreview: React.FC<PatternPreviewProps> = ({
  layers,
  width,
  height,
  backgroundImage,
  isSelectionMode,
  onSelectionComplete,
  selectedLayerId,
  onMaskMove,
  onMaskResize,
  onDimensionsChange,
}) => {
  // 定数とRef
  const MARGIN = 32;
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentPoint, setCurrentPoint] = useState({ x: 0, y: 0 });
  const [isDraggingMask, setIsDraggingMask] = useState(false);
  const [draggedLayerId, setDraggedLayerId] = useState<string | null>(null);
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const patternDefsRef = useRef<Map<string, string>>(new Map());
  const overlayRef = useRef<SVGSVGElement>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [initialMaskState, setInitialMaskState] =
    useState<SelectionArea | null>(null);
  const [selectedClipMaskId, setSelectedClipMaskId] = useState<string | null>(
    null
  );

  // ウィンドウサイズ変更検出用の状態
  const [showResizeWarning, setShowResizeWarning] = useState(false);
  const previousDimensions = useRef({ width, height });
  // 実際のウィンドウサイズを追跡する（初期化時に0を設定し、初回チェックをスキップ）
  const actualWindowSize = useRef({ width: 0, height: 0 });

  // アプリ起動時の警告表示用の状態
  const [showInitialSizeWarning, setShowInitialSizeWarning] = useState(true);

  // ズーム機能用の状態
  const [zoomLevel, setZoomLevel] = useState(100); // 100 = 等倍
  const containerRef = useRef<HTMLDivElement>(null);

  // パン（表示位置移動）用の状態
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStartPosition, setPanStartPosition] = useState({ x: 0, y: 0 });

  // 有効表示領域の計算
  const dimensions = useMemo<PreviewDimensions>(
    () => ({
      width,
      height,
      contentWidth: width - MARGIN * 2,
      contentHeight: height - MARGIN * 2,
      margin: MARGIN,
    }),
    [width, height]
  );

  // ウィンドウサイズ変更の検出
  useEffect(() => {
    // 初期ウィンドウサイズを記録
    actualWindowSize.current = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // ウィンドウサイズの変更を監視するイベントリスナーを設定
    const handleResize = () => {
      const currentWindowWidth = window.innerWidth;
      const currentWindowHeight = window.innerHeight;

      // 実際のウィンドウサイズに変化があった場合のみ警告を表示
      // 5px以上の差があった場合に変更と判定（小さな誤差を無視）
      if (
        actualWindowSize.current.width !== 0 &&
        actualWindowSize.current.height !== 0 &&
        (Math.abs(actualWindowSize.current.width - currentWindowWidth) > 5 ||
          Math.abs(actualWindowSize.current.height - currentWindowHeight) > 5)
      ) {
        setShowResizeWarning(true);
      }

      // 現在のサイズを記録（警告表示の有無に関わらず更新）
      actualWindowSize.current = {
        width: currentWindowWidth,
        height: currentWindowHeight,
      };
    };

    // イベントリスナーを追加
    window.addEventListener('resize', handleResize);

    // コンポーネントのプロパティ変更は記録するが、警告は表示しない
    previousDimensions.current = { width, height };

    // クリーンアップ関数でイベントリスナーを削除
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // アプリ起動時に一度だけ警告を表示
  useEffect(() => {
    // 初期警告はボタンでのみ閉じられるように変更（自動タイマーを削除）
  }, []);

  // 寸法情報が変更されたときに通知
  useEffect(() => {
    onDimensionsChange?.(dimensions);
  }, [dimensions, onDimensionsChange]);

  // パンとズームをリセットする関数
  const resetView = () => {
    setZoomLevel(100);
    setPanPosition({ x: 0, y: 0 });
  };

  // クリッピングマスクをクリックした時の処理
  const handleClipMaskClick = useCallback(
    (layerId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedClipMaskId(layerId);
    },
    []
  );

  // 背景やその他の場所をクリックした時の処理
  const handleBackgroundClick = useCallback(() => {
    setSelectedClipMaskId(null);
  }, []);

  // ホイールでズームを調整する機能
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -5 : 5; // 上下で5%ずつ調整
      setZoomLevel((prev) => {
        const newZoom = Math.max(50, Math.min(300, prev + delta));
        return newZoom;
      });
    }
  };

  // パンのマウスイベントハンドラ
  const handlePanStart = (e: React.MouseEvent<HTMLDivElement>) => {
    // 選択モードやマスク操作中は無視
    if (isSelectionMode || isDraggingMask || resizeHandle) return;

    // マウスキーが押されているか、ズームが100%より大きい場合にパン可能
    if (e.buttons === 1 && (e.altKey || zoomLevel > 100)) {
      setIsPanning(true);
      setPanStartPosition({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y,
      });
      e.preventDefault();
    }
  };

  const handlePanMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning) {
      setPanPosition({
        x: e.clientX - panStartPosition.x,
        y: e.clientY - panStartPosition.y,
      });
      e.preventDefault();
    }
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  // SVGパターンの定義作成
  const createPatternDef = useCallback(
    (svgString: string, color: string, layerId: string) => {
      const defId = `pattern-${layerId}-${color.replace('#', '')}`;

      if (!patternDefsRef.current.has(defId)) {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
        const svgElement = svgDoc.documentElement;

        // viewBox属性の設定
        if (!svgElement.hasAttribute('viewBox')) {
          const width = svgElement.getAttribute('width') || '100';
          const height = svgElement.getAttribute('height') || '100';
          svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        svgElement.removeAttribute('width');
        svgElement.removeAttribute('height');

        // 色の適用
        const rgbaColor = `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(
          color.slice(3, 5),
          16
        )}, ${parseInt(color.slice(5, 7), 16)}, 1.0)`;

        const processElement = (element: Element) => {
          if (element.tagName.toLowerCase() !== 'svg') {
            if (
              !element.hasAttribute('fill') ||
              element.getAttribute('fill') === '' ||
              element.getAttribute('fill') !== 'none'
            ) {
              element.setAttribute('fill', rgbaColor);
            }
            if (
              !element.hasAttribute('stroke') ||
              element.getAttribute('stroke') === ''
            ) {
              element.setAttribute('stroke', 'none');
            } else if (element.getAttribute('stroke') !== 'none') {
              element.setAttribute('stroke', rgbaColor);
            }
          }
          Array.from(element.children).forEach(processElement);
        };

        processElement(svgElement);
        patternDefsRef.current.set(
          defId,
          new XMLSerializer().serializeToString(svgDoc)
        );
      }

      return defId;
    },
    []
  );

  // プレビュー用にパターン要素を生成
  const generatePatternElementsForPreview = useCallback(
    (layer: Layer): PatternElement[] => {
      return generatePatternElements(
        layer,
        dimensions.contentWidth,
        dimensions.contentHeight
      );
    },
    [dimensions]
  );

  // 選択範囲の描画
  const drawSelection = (
    start: { x: number; y: number },
    end: { x: number; y: number }
  ) => {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="none"
        stroke="gray"
        strokeWidth="2"
        strokeDasharray="5,5"
      />
    );
  };

  // マウスイベントを実際の座標に変換するヘルパー関数
  const getAdjustedMousePosition = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!overlayRef.current) return { x: 0, y: 0 };

    const point = overlayRef.current.createSVGPoint();
    point.x = e.clientX;
    point.y = e.clientY;

    const matrix = overlayRef.current.getScreenCTM();
    if (!matrix) return { x: 0, y: 0 };

    // SVG座標系に変換
    const transformedPoint = point.matrixTransform(matrix.inverse());

    // ズームレベルに応じた座標調整
    const scaleAdjustment = 100 / zoomLevel;

    return {
      x: transformedPoint.x * scaleAdjustment,
      y: transformedPoint.y * scaleAdjustment,
    };
  };

  // マウスイベントハンドラ - ズーム考慮
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    // パン中はSVGのイベントを処理しない
    if (isPanning) return;

    if (isSelectionMode && !selectedLayerId) return;

    const svgPoint = getAdjustedMousePosition(e);

    // クリッピングマスクのリサイズハンドルのチェック
    const targetElement = e.target as SVGElement;
    if (targetElement.getAttribute('data-resize-handle')) {
      const layerId = targetElement.getAttribute('data-layer-id');
      const handlePosition = targetElement.getAttribute('data-resize-handle');
      if (layerId && handlePosition) {
        const layer = layers.find((l) => l.id === layerId);
        if (layer?.clipMask) {
          setResizeHandle(handlePosition);
          setDraggedLayerId(layerId);
          setInitialMaskState({ ...layer.clipMask });
          setDragStartOffset({
            x: svgPoint.x,
            y: svgPoint.y,
          });
          e.stopPropagation();
          return;
        }
      }
    }

    // クリッピングマスクのドラッグ開始判定
    if (
      targetElement.tagName === 'rect' &&
      targetElement.getAttribute('data-mask-bounds')
    ) {
      const layerId = targetElement.getAttribute('data-layer-id');
      if (layerId) {
        setSelectedClipMaskId(layerId);
        setIsDraggingMask(true);
        setDraggedLayerId(layerId);
        const layer = layers.find((l) => l.id === layerId);
        if (layer?.clipMask) {
          setDragStartOffset({
            x: svgPoint.x - layer.clipMask.x,
            y: svgPoint.y - layer.clipMask.y,
          });
        }
        e.stopPropagation();
        return;
      }
    }

    // 通常の選択範囲の処理
    if (isSelectionMode) {
      setStartPoint({ x: svgPoint.x, y: svgPoint.y });
      setCurrentPoint({ x: svgPoint.x, y: svgPoint.y });
      setIsDrawing(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    // パン中はSVGのイベントを処理しない
    if (isPanning) return;

    if (!overlayRef.current) return;

    const svgPoint = getAdjustedMousePosition(e);

    if (resizeHandle && draggedLayerId && initialMaskState && onMaskResize) {
      // リサイズ処理
      const deltaX = svgPoint.x - dragStartOffset.x;
      const deltaY = svgPoint.y - dragStartOffset.y;

      const newMask = { ...initialMaskState };

      switch (resizeHandle) {
        case 'nw':
          newMask.x += deltaX;
          newMask.y += deltaY;
          newMask.width -= deltaX;
          newMask.height -= deltaY;
          break;
        case 'ne':
          newMask.y += deltaY;
          newMask.width += deltaX;
          newMask.height -= deltaY;
          break;
        case 'se':
          newMask.width += deltaX;
          newMask.height += deltaY;
          break;
        case 'sw':
          newMask.x += deltaX;
          newMask.width -= deltaX;
          newMask.height += deltaY;
          break;
        // 辺の中央ハンドルの処理
        case 'n':
          newMask.y += deltaY;
          newMask.height -= deltaY;
          break;
        case 'e':
          newMask.width += deltaX;
          break;
        case 's':
          newMask.height += deltaY;
          break;
        case 'w':
          newMask.x += deltaX;
          newMask.width -= deltaX;
          break;
      }

      // 最小サイズを保証
      if (newMask.width >= 10 && newMask.height >= 10) {
        onMaskResize(draggedLayerId, newMask);
      }
    } else if (isDraggingMask && draggedLayerId && onMaskMove) {
      // マスクのドラッグ処理
      onMaskMove(
        draggedLayerId,
        svgPoint.x - dragStartOffset.x,
        svgPoint.y - dragStartOffset.y
      );
    } else if (isDrawing && isSelectionMode) {
      // 通常の選択範囲の処理
      setCurrentPoint({ x: svgPoint.x, y: svgPoint.y });
    }
  };

  const handleMouseUp = () => {
    if (resizeHandle) {
      setResizeHandle(null);
      setDraggedLayerId(null);
      setInitialMaskState(null);
    } else if (isDraggingMask) {
      setIsDraggingMask(false);
      setDraggedLayerId(null);
    } else if (isDrawing && isSelectionMode && selectedLayerId) {
      const area: SelectionArea = {
        x: Math.min(startPoint.x, currentPoint.x),
        y: Math.min(startPoint.y, currentPoint.y),
        width: Math.abs(currentPoint.x - startPoint.x),
        height: Math.abs(currentPoint.y - startPoint.y),
      };
      onSelectionComplete(area, selectedLayerId);
      setIsDrawing(false);
    }
  };

  // リサイズハンドルを描画する関数
  const renderResizeHandles = (layerId: string, mask: SelectionArea) => {
    const handleSize = 8;

    // 頂点と辺で異なるスタイルを適用
    const getHandleStyle = (type: 'corner' | 'edge') => {
      if (type === 'corner') {
        return {
          fill: 'gray',
          stroke: 'gray',
          strokeWidth: '2',
          width: handleSize,
          height: handleSize,
        };
      } else {
        return {
          fill: 'gray',
          stroke: 'gray',
          strokeWidth: '2',
          width: handleSize - 2,
          height: handleSize - 2,
        };
      }
    };

    // position に応じたカーソルスタイル
    const getCursorStyle = (position: string) => {
      const cursorMap = {
        nw: 'nw-resize',
        ne: 'ne-resize',
        se: 'se-resize',
        sw: 'sw-resize',
        n: 'n-resize',
        e: 'e-resize',
        s: 's-resize',
        w: 'w-resize',
      };
      return cursorMap[position as keyof typeof cursorMap] || 'default';
    };

    const handles = [
      // 頂点（4点）- 現在と同じ
      {
        position: 'nw',
        type: 'corner',
        x: mask.x - handleSize / 2,
        y: mask.y - handleSize / 2,
      },
      {
        position: 'ne',
        type: 'corner',
        x: mask.x + mask.width - handleSize / 2,
        y: mask.y - handleSize / 2,
      },
      {
        position: 'se',
        type: 'corner',
        x: mask.x + mask.width - handleSize / 2,
        y: mask.y + mask.height - handleSize / 2,
      },
      {
        position: 'sw',
        type: 'corner',
        x: mask.x - handleSize / 2,
        y: mask.y + mask.height - handleSize / 2,
      },

      // 辺の中央（4点）- 新規追加
      {
        position: 'n',
        type: 'edge',
        x: mask.x + mask.width / 2 - handleSize / 2,
        y: mask.y - handleSize / 2,
      },
      {
        position: 'e',
        type: 'edge',
        x: mask.x + mask.width - handleSize / 2,
        y: mask.y + mask.height / 2 - handleSize / 2,
      },
      {
        position: 's',
        type: 'edge',
        x: mask.x + mask.width / 2 - handleSize / 2,
        y: mask.y + mask.height - handleSize / 2,
      },
      {
        position: 'w',
        type: 'edge',
        x: mask.x - handleSize / 2,
        y: mask.y + mask.height / 2 - handleSize / 2,
      },
    ];

    return handles.map(({ position, type, x, y }) => (
      <rect
        key={`${layerId}-${position}`}
        data-resize-handle={position}
        data-layer-id={layerId}
        x={x}
        y={y}
        {...getHandleStyle(type as 'corner' | 'edge')}
        style={{ cursor: getCursorStyle(position) }}
      />
    ));
  };

  function generateBackgroundImageForPreview(
    backgroundImage: string | null
  ): React.ReactElement | null {
    if (!backgroundImage) {
      return null;
    }

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: '0',
          left: '0',
          overflow: 'hidden',
        }}
      >
        <img
          src={backgroundImage}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
          }}
          alt="Background"
        />
      </div>
    );
  }

  const generateSvgDefs = (layers: Layer[]) => {
    // クリップパス定義の生成
    const clipPathDefs = layers
      .filter((layer) => layer.clipMask)
      .map((layer) => (
        <clipPath key={`clip-${layer.id}`} id={`clip-${layer.id}`}>
          <rect
            // eslint-disable-next-line
            x={layer.clipMask!.x}
            // eslint-disable-next-line
            y={layer.clipMask!.y}
            // eslint-disable-next-line
            width={layer.clipMask!.width}
            // eslint-disable-next-line
            height={layer.clipMask!.height}
          />
        </clipPath>
      ));

    // パターン定義の生成
    const patternDefs = layers
      .filter((layer) => layer.svgPattern)
      .map((layer) => {
        const defId = `pattern-${layer.id}-${layer.parameters.color.replace(
          '#',
          ''
        )}`;
        if (!patternDefsRef.current.has(defId)) {
          // eslint-disable-next-line
          createPatternDef(layer.svgPattern!, layer.parameters.color, layer.id);
        }
        const content = patternDefsRef.current.get(defId);
        return content ? (
          <svg
            key={defId}
            id={defId}
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : null;
      })
      .filter((element) => element !== null);

    return [...clipPathDefs, ...patternDefs];
  };

  return (
    <div
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
      }}
    >
      {/* ズームコントロール - 右上に配置 */}
      <div className="absolute top-2 right-2 bg-white p-2 rounded-md shadow-sm z-10 flex items-center space-x-2">
        <button
          onClick={resetView}
          className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
          title="等倍に戻す"
        >
          リセット
        </button>
        <input
          type="range"
          min="50"
          max="300"
          value={zoomLevel}
          onChange={(e) => setZoomLevel(parseInt(e.target.value))}
          className="w-24 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <span className="text-xs font-medium">{zoomLevel}%</span>
      </div>

      {/* パンのヒント表示 */}
      {zoomLevel > 100 && (
        <div className="absolute top-2 left-2 bg-white bg-opacity-75 p-1 rounded text-xs text-gray-600">
          ドラッグで表示位置を移動できます
        </div>
      )}

      {/* プレビュー表示エリア - ズームとパンを適用 */}
      <div
        className="relative"
        style={{
          width: `${dimensions.contentWidth}px`,
          height: `${dimensions.contentHeight}px`,
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
          margin: `${MARGIN}px`,
        }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        onWheel={handleWheel}
      >
        {/* ズームとパンが適用されるコンテナ */}
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            transform: `scale(${zoomLevel / 100}) translate(${
              panPosition.x / (zoomLevel / 100)
            }px, ${panPosition.y / (zoomLevel / 100)}px)`,
            transformOrigin: 'center center',
            transition: isPanning ? 'none' : 'transform 0.1s ease-out',
            cursor: isPanning
              ? 'grabbing'
              : zoomLevel > 100
              ? 'grab'
              : 'default',
          }}
        >
          {/* 背景画像 - object-fitを使って正確に表示 */}
          {generateBackgroundImageForPreview(backgroundImage)}

          {/* パターンを描画するSVG（blend-mode: darken、マウスイベントなし） */}
          <svg
            ref={svgRef}
            width={dimensions.contentWidth}
            height={dimensions.contentHeight}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              mixBlendMode: 'darken',
              pointerEvents: 'none',
            }}
          >
            <defs>{generateSvgDefs(layers)}</defs>

            {/* レイヤーごとのパターン描画 */}
            {layers.map((layer) => {
              if (!layer.svgPattern || !layer.visible) return null;

              const defId = `pattern-${
                layer.id
              }-${layer.parameters.color.replace('#', '')}`;

              // パターン要素の生成
              const patterns = generatePatternElementsForPreview(layer);

              // レイヤーの回転
              const angleView = layer.parameters.angleView;

              // 中心点の計算
              const centerX = dimensions.contentWidth / 2;
              const centerY = dimensions.contentHeight / 2;

              return (
                <g key={layer.id} style={{ mixBlendMode: 'darken' }}>
                  {/* クリッピングマスクとパターンの描画をグループ化 */}
                  <g
                    clipPath={
                      layer.clipMask ? `url(#clip-${layer.id})` : undefined
                    }
                  >
                    {/* 回転を考慮してパターンを描画 */}
                    <g
                      transform={`translate(${centerX} ${centerY}) rotate(${angleView}) translate(${-centerX} ${-centerY})`}
                    >
                      {patterns.map(({ key, size, transform }) => (
                        <use
                          key={key}
                          href={`#${defId}`}
                          width={size}
                          height={size}
                          transform={transform}
                        />
                      ))}
                    </g>
                  </g>
                </g>
              );
            })}
          </svg>

          {/* オーバーレイSVG: blend-mode なし、選択UI・マウスイベント担当 */}
          <svg
            ref={overlayRef}
            width={dimensions.contentWidth}
            height={dimensions.contentHeight}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={handleBackgroundClick}
          >
            {/* clipMask がある各レイヤーに透明な当たり判定 rect を配置 */}
            {layers
              .filter((layer) => layer.clipMask && layer.visible)
              .map((layer) => (
                <rect
                  key={`hit-${layer.id}`}
                  // eslint-disable-next-line
                  x={layer.clipMask!.x}
                  // eslint-disable-next-line
                  y={layer.clipMask!.y}
                  // eslint-disable-next-line
                  width={layer.clipMask!.width}
                  // eslint-disable-next-line
                  height={layer.clipMask!.height}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => handleClipMaskClick(layer.id, e)}
                />
              ))}

            {/* 選択レイヤー（リサイズハンドル・マスク枠・選択矩形） */}
            <SelectionOverlay
              layers={layers}
              selectedClipMaskId={selectedClipMaskId}
              handleClipMaskClick={handleClipMaskClick}
              renderResizeHandles={renderResizeHandles}
              isSelectionMode={isSelectionMode}
              isDrawing={isDrawing}
              drawSelection={drawSelection}
              startPoint={startPoint}
              currentPoint={currentPoint}
            />
          </svg>
        </div>
      </div>

      {/* ウィンドウサイズ変更警告 - プレビュー画面中央にモーダル表示 */}
      {showResizeWarning && (
        <div className="absolute inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-start">
              <div className="flex-shrink-0 text-yellow-400 text-2xl">⚠️</div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  ウィンドウサイズが変更されました
                </h3>
                <div className="mt-2 text-sm text-gray-500">
                  <p className="mb-2">
                    このアプリケーションは固定サイズでの利用を推奨しています。
                  </p>
                  <p className="mb-2">
                    ウィンドウサイズの変更により、地紋パターンの表示や位置が崩れる場合があります。
                  </p>
                  <p>
                    表示サイズを調整したい場合は、画面右上のズーム機能をお使いください。
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setShowResizeWarning(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150"
              >
                理解しました
              </button>
            </div>
          </div>
        </div>
      )}

      {/* アプリ起動時の初期警告 - モーダルとして表示 */}
      {showInitialSizeWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-start">
              <div className="flex-shrink-0 text-yellow-400 text-2xl">⚠️</div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  サイズ変更に関する重要なお知らせ
                </h3>
                <div className="mt-2 text-sm text-gray-500">
                  <p className="mb-2">
                    このアプリケーションは表示サイズを変更せずにお使いいただくことを強く推奨します。
                  </p>
                  <p className="mb-2">
                    現在のサイズ：{window.innerWidth}×{window.innerHeight}
                    ピクセル
                  </p>
                  <p className="mb-2">
                    ブラウザウィンドウのサイズを変更すると、地紋パターンの表示や位置が崩れる場合があります。
                  </p>
                  <p>必要な場合は画面右上のズーム機能をお使いください。</p>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setShowInitialSizeWarning(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150"
              >
                理解しました
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternPreview;
